import * as React from "react"
import * as CheckboxPrimitive from "@radix-ui/react-checkbox"
import { Check } from "lucide-react";

import { cn } from "@/lib/utils"
import { useWarnIfUnnamed } from "@/components/ui/field"

type CheckboxProps = React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> & {
  /** The name when no visible `<Label htmlFor>` sits beside it. Becomes `aria-label`. */
  label?: string
}

const Checkbox = React.forwardRef<React.ElementRef<typeof CheckboxPrimitive.Root>, CheckboxProps>(
  ({ className, label, ...props }, forwardedRef) => {
  const innerRef = React.useRef<HTMLButtonElement | null>(null)
  useWarnIfUnnamed(innerRef, "Checkbox")
  const ref = React.useCallback(
    (node: HTMLButtonElement | null) => {
      innerRef.current = node
      if (typeof forwardedRef === "function") forwardedRef(node)
      else if (forwardedRef) forwardedRef.current = node
    },
    [forwardedRef],
  )
  return (
  <CheckboxPrimitive.Root
    ref={ref}
    // Safari Tabs past a <button> unless it sets tabindex (see ui/select). A caller's own tabIndex wins.
    tabIndex={0}
    aria-label={props["aria-label"] ?? label}
    className={cn(
      // border-control-border, not border-input: --input is a near-white fill and
      // measured 1.02:1 against the cream card, so the unchecked box was invisible.
      //
      // rounded-xs (4px), not rounded-md: `md` is the 14px inner-panel rung,
      // and 14px on a 20px box is a CIRCLE — "Remember me" read as a radio
      // button, the one control a checkbox must never be mistaken for (A14).
      // A square with softened corners is what a checkbox looks like on every
      // platform.
      "peer h-5 w-5 shrink-0 rounded-xs border-2 border-control-border ring-offset-background focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground data-[state=checked]:border-primary transition-colors",
      // 20px box. Bare, that is under WCAG 2.2 SC 2.5.8's 24px minimum and far
      // under a fingertip; hit-target lifts the touch area to 44px on coarse
      // pointers while leaving the drawn box at 20px.
      "hit-target",
      className
    )}
    {...props}
  >
    <CheckboxPrimitive.Indicator
      className={cn("flex items-center justify-center text-current")}
    >
      <Check className="h-4 w-4 stroke-[3]" />
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
  )
})
Checkbox.displayName = CheckboxPrimitive.Root.displayName

export { Checkbox }
export type { CheckboxProps }
